import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Page, Group, Layout, Button, ExcelUpload } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import {
    APIS,
    SF_APP_DTL_VRFN_INFO,
    SF_APP_RGST_VRFN_RSLT,
} from "@/tz/go/egaz/osci/lpco/app/service/OsciLpcoAprvService";
import { UsrLstbyRole } from "../../comn/UsrLstbyRole";
import { OgaAprvRqst } from "@/comn/comn/aprv/OgaAprvRqst";
import { SF_APRV_RQST } from "@/comn/comn/aprv/service/AprvService";

/*
 * LPCO Application Verification Result
 * !== LPCO 신청서 심사 결과 ==!
 */
export const OSCI0201005S = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0006", "auth_osci0015"],
    });

    /*
     * Code Resource
     * !== 사용할 Code 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0131" },
            { area: "comnCd", comnCd: "OSC0142" },
        ],
    });

    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    const { close, postMessage, getParams } = usePopup();
    const params = getParams(); // 부모창에서 전달된 Parameter

    let errmsg: any;

    const [sbmtVisible, setSbmtVisible] = useState(false);
    const [exmpnTaxVisible, setExmpnTaxVisble] = useState(false);

    const [excludeAuditCode, setExcludeAuditCode] = useState<any[]>(["FV", "IP", "AP", "RE", "CR"]);

    /* 2026-05-06 KNH: Inspection Officer Assignment !== 검사담당자 배정 추가 ==! */
    const [asnmtOfcrVisible, setAsnmtOfcrVisible] = useState(false);
    const asnmtOfcrInfoRef = useRef<{ asnmtOfcrNm: string; asnmtOfcrId: string }>({
        asnmtOfcrNm: "",
        asnmtOfcrId: "",
    });

    const [aprvExisYn, setAprvExisYn] = useState("N");
    const [aprvTargetStat, setAprvTargetStat] = useState<string[]>([]);

    const form = {
        appDtlVrfnInfo: useForm({
            defaultSchema: SF_APP_DTL_VRFN_INFO,
        }),
        appRgstVrfnRslt: useForm({
            defaultSchema: SF_APP_RGST_VRFN_RSLT,
        }),

        aprvRqstForm: useForm({
            defaultSchema: SF_APRV_RQST,
            defaultValues: {
                ptlTpCd: "O", // 포털 구분 코드 (I: 내부, O: 외부)
                bsopClsfCd: "OSC", // 업무 구분 코드
                scrnDocCd: "scrn_osca0001", // Approval review screen 029Q !== 결재 검토화면 029Q ==!
                aprvReffId: "/" + params.appInfo.appId, // 결재 요청 문서 Key 값
                docNo: params.appInfo.appNo, // Application number as document number !== 문서 번호로 신청서 번호 사용 ==!
                aprvAfPrcssCfgnId: "cfgn_osc0001", // 결재완료 후처리 ID (결재후처리설정 테이블에 등록된 후처리ID)
                // rjctAfPrcssCfgnId: "cfgn_osc0002", // 거절(reject) 후처리 ID (결재후처리설정 테이블에 등록된 후처리ID)
                cnclAfPrcssCfgnId: "cfgn_osc0003", // 결재취소 후처리 ID (결재후처리설정 테이블에 등록된 후처리ID)
                rtrnAfPrcssCfgnId: "cfgn_osc0004", // 반려 후처리 ID (결재후처리설정 테이블에 등록된 후처리ID)
                prevStageRtrnUseYn: "Y", // 이전단계 반려처리가 필요한 경우에만 'Y'로 넘겨 줄 것 (결재처리 시 이전단계반려 라디오버튼을 보여줌)
                itsfAprvYn: "N", // 업무팀 자체 결재처리 시 'Y'로 넘겨 줄 것 (결재처리탭 비활성화)
                rjctStatUseYn: "N", // 거절 상태 사용 여부
                grpAprvYn: "N", // 그룹 결재 여부
            },
        }),
    };

    /*
     * Application Item Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Application Information !== 신청서 정보 조회 ==!.
        getAppAprvInfo: useFetch({
            api: (data) => APIS.getAppAprvInfo({ reffId: data.appId }),
            onSuccess: (data) => {
                console.log("getAppAprvInfo data", data);
                if (data.appAprvInfo!) {
                    if (data.appAprvInfo.content.prcssStatCd!) {
                        if ("VF" == data.appAprvInfo.content.prcssStatCd && "EV" == params.appInfo.appStatCd) {
                            setExmpnTaxVisble(true);
                        }
                        setSbmtVisible(true);
                    }
                    form.appRgstVrfnRslt.setValues(data.appAprvInfo.content);

                    // 2026-05-08 KNH: Set previous approval ID !== 기존 결재 ID 설정 ==!
                    if (data?.appAprvInfo?.content?.aprvId) {
                        form.aprvRqstForm.setValue("aprvId", data?.appAprvInfo?.content?.aprvId);
                    }
                }

                const prcsRoleTpCd = data?.prcsInfo?.content?.prcsRoleTpCd || "";
                const prevPrcsRoleTpCd = data?.prcsInfo?.content?.prevPrcsRoleTpCd || "";
                setExcludeAuditCode([
                    ...APIS.getExcludeAuditRsltCdList(prcsRoleTpCd, prevPrcsRoleTpCd, params.appInfo.appCdNo),
                ]);

                // 2026-05-07 KNH: Check approval existence !== 승인 존재 여부 설정 ==!
                const aprvExisYn = data?.prcsInfo?.content?.aprvExisYn || "N";
                setAprvExisYn(aprvExisYn);

                // Approval-gating target result states, from backend !== 전자결재 대상 결과 상태집합(백엔드 수신) ==!
                setAprvTargetStat(data?.prcsInfo?.content?.aprvTargetStatList || []);

                /* 2026-05-06 KNH: Inspection Officer Assignment !== 검사담당자 배정 추가 ==! */
                // console.log('getAppAprvInfo asnmtOfcrYn',data?.asnmtOfcrYn?.content);
                form.appRgstVrfnRslt.setValue("asnmtOfcrYn", data?.asnmtOfcrYn?.content);

                // console.log('getAppAprvInfo asnmtOfcrInfo',data?.asnmtOfcrInfo?.content);
                if (data?.asnmtOfcrYn?.content === "Y" && data?.asnmtOfcrInfo?.content) {
                    const asnmtOfcrNm = data?.asnmtOfcrInfo?.content?.asnmtOfcrNm || "";
                    const asnmtOfcrId = data?.asnmtOfcrInfo?.content?.asnmtOfcrId || "";

                    form.appRgstVrfnRslt.setValue("asnmtOfcrNm", asnmtOfcrNm);
                    form.appRgstVrfnRslt.setValue("asnmtOfcrId", asnmtOfcrId);

                    asnmtOfcrInfoRef.current = {
                        asnmtOfcrNm: asnmtOfcrNm,
                        asnmtOfcrId: asnmtOfcrId,
                    };
                }

                handler.checkAsnmtOfcrVisible(data.appAprvInfo.content.prcssStatCd);
            },
            onError: (error) => {
                errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
        saveAppAprvRslt: useFetch({
            api: (data) => {
                return APIS.saveAppAprvRslt({ ...data });
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        handler.setInit();
                        modal.postMessage({ rslt: "save" });
                    },
                    onClose: () => {
                        handler.setInit();
                    },
                });
            },
            onError: (error) => {
                const errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
        // Submit Application Verification Result !== 신청서 심사결과 제출 ==!
        sbmtAppAprvRslt: useFetch({
            api: (data) => {
                return APIS.sbmtAppAprvRslt({ ...data });
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        modal.postMessage({ rslt: "sbmt" });
                    },
                    onClose: () => {
                        modal.postMessage({ rslt: "sbmt" });
                    },
                });
            },
            onError: (error) => {
                const errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
        // Save Verification Result Approval Request !== 심사 결과 결재 요청 등록 ==!
        rqstAprvRslt: useFetch({
            api: (data) => {
                console.log("rqstAprvRslt data", data);
                return APIS.rqstAprvRslt({ ...data });
            },
            onSuccess: (data) => {
                // TODO KNH: !== 승인 요청 성공 시 처리 ==!
                // Close the approval popup only on success; on failure it stays open !== 결재 팝업은 성공 시에만 닫고 실패 시 유지 ==!
                modal.closeModal("OgaAprvRqst");
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        modal.postMessage({ rslt: "sbmt" });
                    },
                    onClose: () => {
                        modal.postMessage({ rslt: "sbmt" });
                    },
                });
            },
            onError: (error) => {
                // No response (network error, server restart) also shows the default error !== 응답이 없는 경우(네트워크 오류, 서버 재기동)도 기본 오류 메시지 표시 ==!
                const errmsg =
                    !error.response?.data?.errorMessage || error.response.data.errorCode == -1
                        ? "msg.osc.00015"
                        : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
    };

    /*
     * Application Item Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        setInit: async () => {
            await form.appDtlVrfnInfo.setValues(params.appInfo);
            await handler.getAppAprvInfo();
        },
        getAppAprvInfo: async () => {
            fetch.getAppAprvInfo.fetch(params.appInfo);
        },
        changePrcssStatCd: (value: any) => {
            if ("VF" == value && "EV" == params.appInfo.prcsRoleTpCd) {
                setExmpnTaxVisble(true);
            } else {
                setExmpnTaxVisble(false);
            }

            handler.checkAsnmtOfcrVisible(value);
        },
        // Registration validation of application verification results !== 신청서 심사결과 등록 유효성 검사 ==!
        validateRgstVrfnRslt: async (type: any) => {
            const chkRslt = await form.appRgstVrfnRslt.validate();

            if (chkRslt) {
                form.appRgstVrfnRslt.setValue("reffId", params.appInfo.appId);
                const data = { ...form.appRgstVrfnRslt.getValues() };
                data.amdTpCd = params.appInfo.amdTpCd;
                data.orgCd = auth.get("orgCd");

                if (type == "save") {
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            fetch.saveAppAprvRslt.fetch(data);
                        },
                    });
                } else if (type == "sbmt") {
                    modal.openModal({
                        content: "msg.osc.00092",
                        onConfirm: () => {
                            // 2026-05-08 KNH: Check approval existence and processing status !== 결재를 해야하고 대상 결과 상태일 경우 결재 팝업 표시 ==!
                            if (
                                aprvExisYn == "Y" &&
                                aprvTargetStat.includes(form.appRgstVrfnRslt.getValue("prcssStatCd"))
                            ) {
                                handler.openAprvRqstPopup();
                            } else {
                                fetch.sbmtAppAprvRslt.fetch(data);
                            }
                        },
                    });
                }
            }
        },
        // Assignment Inspection Officer Selected !== 검사담당자 선택 ==!
        onOfcrSelected: (data: any) => {
            form.appRgstVrfnRslt.setValue("asnmtOfcrNm", data.label);
            form.appRgstVrfnRslt.setValue("asnmtOfcrId", data.code);
        },

        // Check Inspection Officer Assignment Visible !== 검사담당자 배정 표시 여부 체크 ==!
        checkAsnmtOfcrVisible: (prcssStatCd: any) => {
            // console.log('checkAsnmtOfcrVisible prcssStatCd',prcssStatCd);
            let isAsnmtVisible = false;
            if ("VF" == prcssStatCd && form.appRgstVrfnRslt.getValues()?.asnmtOfcrYn === "Y") {
                isAsnmtVisible = true;
                if (asnmtOfcrInfoRef.current.asnmtOfcrId) {
                    form.appRgstVrfnRslt.setValue("asnmtOfcrNm", asnmtOfcrInfoRef.current.asnmtOfcrNm);
                    form.appRgstVrfnRslt.setValue("asnmtOfcrId", asnmtOfcrInfoRef.current.asnmtOfcrId);
                }
            } else {
                isAsnmtVisible = false;
                asnmtOfcrInfoRef.current = {
                    asnmtOfcrNm: form.appRgstVrfnRslt.getValue("asnmtOfcrNm") || "",
                    asnmtOfcrId: form.appRgstVrfnRslt.getValue("asnmtOfcrId") || "",
                };

                form.appRgstVrfnRslt.setValue("asnmtOfcrNm", "");
                form.appRgstVrfnRslt.setValue("asnmtOfcrId", "");
            }
            // !== 표시될 때만 필수 ==!
            form.appRgstVrfnRslt.schema.asnmtOfcrNm.required = isAsnmtVisible;

            setAsnmtOfcrVisible(isAsnmtVisible);
        },

        // Open Approval Request Popup !== 결재 요청 팝업 오픈 ==!
        openAprvRqstPopup: () => {
            modal.openModal({
                content: (
                    <OgaAprvRqst
                        paramData={{
                            bsopClsfCd: "OSC",
                            scrnDocCd: "scrn_osca0001",
                            aprvId: form.aprvRqstForm.getValue("aprvId"),
                        }}
                        callbackFn={(aprvData: any) => {
                            if (aprvData) {
                                if (aprvData) {
                                    form.aprvRqstForm.setValues({
                                        aprvRqstTtleNm: aprvData.aprvRqstTtleNm,
                                        aprvRqstCn: aprvData.aprvRqstCn,
                                        usrDmnId: aprvData.usrDmnId,
                                        aprvPrcssPathMgmtId: aprvData.aprvPrcssPathMgmtId,
                                    });
                                }
                                const param = {
                                    ...form.appRgstVrfnRslt.getValues(),
                                    aprvRqstDto: form.aprvRqstForm.getValues(),
                                };
                                // Return the request so the popup can disable its button; popup is closed in onSuccess !== 팝업 버튼 비활성을 위해 요청을 반환, 팝업은 onSuccess에서 닫음 ==!
                                return fetch.rqstAprvRslt.fetch(param);
                            }
                            modal.closeModal("OgaAprvRqst");
                        }}
                    />
                ),
                size: "md",
                draggable: true,
                close: false,
                id: "OgaAprvRqst",
            });
        },
    };

    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page>
            {/* <form> */}
            <Group>
                <Group.Body>
                    <Layout direction="col">
                        <Layout>
                            <Layout.Left>
                                <Group.Title title={"T_VRFN_RSLT"} titleSize={1}></Group.Title>
                            </Layout.Left>
                        </Layout>

                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.appDtlVrfnInfo.schema.appNo} />
                                <Group.Control {...form.appDtlVrfnInfo.schema.ucrNo} />
                            </Group.Row>

                            <Group.Row>
                                <Group.Control {...form.appDtlVrfnInfo.schema.appNm} />
                                <Group.Control {...form.appDtlVrfnInfo.schema.sbmtDtm} />
                            </Group.Row>

                            <Group.Row>
                                <Group.Control
                                    {...form.appRgstVrfnRslt.schema.prcssStatCd}
                                    controlSize={10}
                                    excludes={[...excludeAuditCode]}
                                    onChange={(value) => {
                                        handler.changePrcssStatCd(value);
                                    }}
                                />
                            </Group.Row>

                            <Group.Row>
                                <Group.Control {...form.appRgstVrfnRslt.schema.prcssRsnCn} controlSize={10} />
                            </Group.Row>

                            {exmpnTaxVisible && (
                                <Group.Row>
                                    <Group.Control {...form.appRgstVrfnRslt.schema.exmpnTaxTpCd} controlSize={10} />
                                </Group.Row>
                            )}

                            {/* 2026-05-06 KNH: Inspection Officer Assignment !== 검사담당자 배정 추가 ==! */}
                            {asnmtOfcrVisible && (
                                <Group.Row>
                                    <Group.Control
                                        {...form.appRgstVrfnRslt.schema.asnmtOfcrNm}
                                        controlSize={10}
                                        rightButton={{
                                            icon: "search",
                                            onClick: () => {
                                                const popupParams = {
                                                    asnmtOfcrTpCd: "IP", // Inspection Officer !== 검사담당자 ==!
                                                    asnmtBsopTpCd: "ABO03", // LPCO
                                                    asnmtOrgCd: auth.get("orgCd"),
                                                    excuUserId: auth.get("userId"),
                                                    appCdNo: form.appDtlVrfnInfo.getValues()?.appCdNo,
                                                };

                                                modal.openModal({
                                                    content: (
                                                        <UsrLstbyRole
                                                            params={{ ...popupParams }}
                                                            onSelected={handler.onOfcrSelected}
                                                        />
                                                    ),
                                                    size: "md",
                                                    draggable: true,
                                                });
                                            },
                                        }}
                                    />
                                </Group.Row>
                            )}

                            <Group.Row>
                                <Group.Control {...form.appRgstVrfnRslt.schema.rmrk} controlSize={10} />
                            </Group.Row>
                        </Group.Section>
                    </Layout>

                    <Layout direction="row">
                        <Layout.Right>
                            <Button
                                role="save"
                                onClick={() => {
                                    handler.validateRgstVrfnRslt("save");
                                }}
                            />
                            {sbmtVisible && (
                                <Button
                                    role="submit"
                                    onClick={() => {
                                        handler.validateRgstVrfnRslt("sbmt");
                                    }}
                                >
                                    {t("L_CFRM")}
                                </Button>
                            )}
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
        </Page>
    );
};
